export type MotionStatus = "completed" | "cancelled" | "skipped"

export interface MotionResult {
  status: MotionStatus
  target: string
  preset: string
}

export interface MotionAnimationLike {
  finished: Promise<unknown>
  cancel(): void
}

export interface MotionElementLike {
  animate(keyframes: Keyframe[], options: KeyframeAnimationOptions): MotionAnimationLike
}

/**
 * Minimal DOM surface required to resolve a motion target.
 *
 * Keep this narrower than ParentNode: consumers that combine browser DOM types
 * with edge-runtime globals can merge incompatible Element.append signatures,
 * even though querySelectorAll remains compatible.
 */
export interface MotionRootLike {
  querySelectorAll<E extends Element = Element>(selectors: string): NodeListOf<E>
}

export type MotionElementResolver = (
  blockId: string | null,
  target: string,
  root?: MotionRootLike | null,
) => MotionElementLike | null

export function resolveMotionBlockId(blockKey: string, block?: { meta?: { namespace?: string } }): string {
  return String(block?.meta?.namespace ?? blockKey)
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .toLowerCase()
}

export class MotionRegistry {
  private readonly active = new WeakMap<MotionElementLike, MotionAnimationLike>()

  constructor(private readonly resolveElement: MotionElementResolver = resolveDomElement) {}

  async run(blockId: string | null, params: Record<string, unknown>, root?: MotionRootLike | null): Promise<MotionResult> {
    const target = String(params.target ?? "")
    const preset = String(params.preset ?? "")
    if (!target || !preset) return { status: "skipped", target, preset }

    const element = this.resolveElement(blockId, target, root)
    if (!element) return { status: "skipped", target, preset }

    const reducedMotion = String(params.reducedMotion ?? "instant")
    const prefersReduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (prefersReduced && reducedMotion === "skip") {
      return { status: "skipped", target, preset }
    }

    const duration = prefersReduced ? 0 : Math.max(0, Number(params.durationMs ?? 200) || 0)
    const fill = normalizeFill(params.fill)
    const previous = this.active.get(element)
    previous?.cancel()

    const animation = element.animate(keyframesForPreset(preset), {
      duration,
      easing: String(params.easing ?? "ease-out"),
      fill,
    })
    this.active.set(element, animation)

    try {
      await animation.finished
      return { status: "completed", target, preset }
    } catch (error) {
      if (this.active.get(element) !== animation) {
        return { status: "cancelled", target, preset }
      }
      throw error
    } finally {
      if (this.active.get(element) === animation) this.active.delete(element)
    }
  }
}

export function keyframesForPreset(preset: string): Keyframe[] {
  switch (preset) {
    case "fade-in":
      return [{ opacity: 0 }, { opacity: 1 }]
    case "fade-out":
      return [{ opacity: 1 }, { opacity: 0 }]
    case "slide-in":
      return [
        { opacity: 0, transform: "translateY(12px)" },
        { opacity: 1, transform: "translateY(0)" },
      ]
    case "slide-out":
      return [
        { opacity: 1, transform: "translateY(0)" },
        { opacity: 0, transform: "translateY(-12px)" },
      ]
    case "scale-in":
      return [
        { opacity: 0, transform: "scale(0.96)" },
        { opacity: 1, transform: "scale(1)" },
      ]
    case "scale-out":
      return [
        { opacity: 1, transform: "scale(1)" },
        { opacity: 0, transform: "scale(0.96)" },
      ]
    case "pulse":
      return [{ transform: "scale(1)" }, { transform: "scale(1.03)" }, { transform: "scale(1)" }]
    default:
      throw new Error(`Unknown motion preset "${preset}"`)
  }
}

function normalizeFill(value: unknown): FillMode {
  const fill = String(value ?? "both")
  return fill === "none" || fill === "forwards" || fill === "backwards" || fill === "both" ? fill : "both"
}

function resolveDomElement(blockId: string | null, target: string, root?: MotionRootLike | null): MotionElementLike | null {
  if (typeof document === "undefined") return null
  const scope = root ?? document
  const candidates = Array.from(scope.querySelectorAll<HTMLElement>("[data-cogo-element-id]")).filter(
    (element) => element.getAttribute("data-cogo-element-id") === target,
  )

  if (blockId) {
    const scoped = candidates.find((element) => {
      const block = element.closest("[data-cogo-block-namespace], [data-cogo-block-id]")
      return (block?.getAttribute("data-cogo-block-namespace") ?? block?.getAttribute("data-cogo-block-id")) === blockId
    })
    const rendered = resolveRenderedElement(scoped)
    if (rendered) return rendered
    return null
  }
  if (candidates.length === 1) {
    const rendered = resolveRenderedElement(candidates[0])
    if (rendered) return rendered
  }
  const byId = document.getElementById(target)
  const isInsideScope = !root || !(root instanceof Node) || (byId != null && root.contains(byId))
  return isInsideScope ? resolveRenderedElement(byId) : null
}

function resolveRenderedElement(element: Element | null | undefined): MotionElementLike | null {
  let current = element
  const visited = new Set<Element>()
  while (current && isDisplayContents(current)) {
    if (visited.has(current)) return null
    visited.add(current)
    current = current.firstElementChild
  }
  return isMotionElement(current) ? current : null
}

function isDisplayContents(element: Element): boolean {
  if (!(element instanceof HTMLElement)) return false
  if (element.style.display === "contents" || element.classList.contains("contents")) return true
  return typeof window !== "undefined" && window.getComputedStyle(element).display === "contents"
}

function isMotionElement(value: unknown): value is MotionElementLike {
  return typeof value === "object" && value !== null && typeof (value as { animate?: unknown }).animate === "function"
}
